import { CoreInterfaces, DTOs } from "src/core/Models";
import * as Constants from "./../core/Constants";
import {
  findQuestionInService,
  resolveTaskDisplayedAllocationOfResponsibility,
} from "./shared";
import {
  getFrequencyMultiplicator,
  getServiceVariable,
  getTaskCalculationMultiplicator,
  getTaskCalculationVariable,
} from "./calculation/service-stats";
import { GridRowOrderChangeParams } from "@mui/x-data-grid-pro";
import { AdditionalCostDTO } from "src/core/Models/DTO.interface";

export function getTaskEstimatedTimePerUnit(
  taskDTO: DTOs.TaskDTO,
  serviceDTO?: DTOs.ServiceDTO
): number {
  let estimatedTimePerUnit =
    taskDTO.data.adjustments.estimatedTimePerUnit ??
    taskDTO.data.estimatedTimePerUnit;

  if (!serviceDTO) {
    return Number(estimatedTimePerUnit) ?? 0;
  }
  const getQ0701Value = () => {
    const q = findQuestionInService(
      serviceDTO,
      Constants.PayrollAndExpenseAndTravelInvoiceManagementQuestions.Q0701
    );
    return Number(q?.data?.userValue ?? 0);
  };

  switch (taskDTO.data.code) {
    case Constants.PayrollAndExpenseAndTravelInvoiceManagementTasks.T0705: {
      const value = getQ0701Value();

      const thresholds = [
        { AmountMin: 1, AmountMax: 10, BasicEstimatedTime: 5 },
        { AmountMin: 11, AmountMax: 20, BasicEstimatedTime: 4.5 },
        { AmountMin: 21, AmountMax: 30, BasicEstimatedTime: 4 },
        { AmountMin: 31, AmountMax: 50, BasicEstimatedTime: 3.5 },
        { AmountMin: 51, AmountMax: 70, BasicEstimatedTime: 3 },
        {
          AmountMin: 71,
          AmountMax: Number.MAX_SAFE_INTEGER,
          BasicEstimatedTime: 2.5,
          FlexHRMPrice: 50,
          WorkerPrice: 20,
        },
      ];

      const matched = thresholds.find(
        (t) => value >= t.AmountMin && value <= t.AmountMax
      );

      estimatedTimePerUnit = matched?.BasicEstimatedTime ?? 0;

      break;
    }
    case Constants.PayrollAndExpenseAndTravelInvoiceManagementTasks.T0712:
    case Constants.PayrollAndExpenseAndTravelInvoiceManagementTasks.T0714: {
      const value = getQ0701Value();

      const thresholds = [
        { AmountMin: 1, AmountMax: 10, BasicEstimatedTime: 2 },
        { AmountMin: 11, AmountMax: 40, BasicEstimatedTime: 1.5 },
        {
          AmountMin: 41,
          AmountMax: Number.MAX_SAFE_INTEGER,
          BasicEstimatedTime: 1,
        },
      ];

      const matched = thresholds.find(
        (t) => value >= t.AmountMin && value <= t.AmountMax
      );

      estimatedTimePerUnit = matched?.BasicEstimatedTime ?? 0;

      break;
    }
    default:
      break;
  }

  return (
    Number(
      taskDTO.data.adjustments.estimatedTimePerUnit ?? estimatedTimePerUnit
    ) ?? 0
  );
}

export const resolveCostWithinMinMax = (
  additionalCost: AdditionalCostDTO,
  usedValue: number | null
): number => {
  switch (additionalCost.data.code) {
    case Constants.PayrollAndExpenseAndTravelInvoiceManagementAdditionalCosts
      .T0755:
    case Constants.PayrollAndExpenseAndTravelInvoiceManagementAdditionalCosts
      .T0757:
      if (
        usedValue >= 1 &&
        usedValue <= Number(Constants.HelpfulConstants.MinCostMaxThreshold)
      ) {
        return additionalCost.data.maxCost;
      } else if (
        usedValue > Number(Constants.HelpfulConstants.MinCostMaxThreshold)
      ) {
        return additionalCost.data.minCost;
      }
  }
};

const calculationMethodProcessorsMap: {
  [method in Constants.TaskCalculationMethod]?: (
    serviceDTO: DTOs.ServiceDTO,
    taskDTO: DTOs.TaskDTO
  ) => number;
} = {
  [Constants.TaskCalculationMethod.AmountXTimeXPricePerHour]: (
    serviceDTO: DTOs.ServiceDTO,
    taskDTO: DTOs.TaskDTO
  ) => {
    if (
      resolveTaskDisplayedAllocationOfResponsibility(taskDTO) ===
      Constants.TaskAllocationResponsibility.Client
    ) {
      return 0;
    }
    const amountVariable = getServiceVariable(
      serviceDTO,
      Constants.CalculationVariable.Amount
    );
    const calculationVariable = getFrequencyMultiplicator(taskDTO, serviceDTO);
    const amount = calculationVariable ?? amountVariable ?? 0;
    const estimatedTimePerUnit = getTaskEstimatedTimePerUnit(
      taskDTO,
      serviceDTO
    );
    return Math.ceil(amount * estimatedTimePerUnit);
  },
  [Constants.TaskCalculationMethod.FrequencyXTimeXPricePerHour]: (
    serviceDTO: DTOs.ServiceDTO,
    taskDTO: DTOs.TaskDTO
  ) => {
    if (
      resolveTaskDisplayedAllocationOfResponsibility(taskDTO) ===
      Constants.TaskAllocationResponsibility.Client
    ) {
      return 0;
    }
    const estimatedTimePerUnit = getTaskEstimatedTimePerUnit(taskDTO);
    if (taskDTO.data.calculationTask) {
      return Math.ceil(
        estimatedTimePerUnit *
          getTaskCalculationMultiplicator(serviceDTO, taskDTO)
      );
    } else {
      const frequency = !!taskDTO.data.adjustments.frequency
        ? taskDTO.data.adjustments.frequency
        : taskDTO.data.frequency;
      if (!!frequency) {
        const frequencyValue = Constants.FrequencyValueMap[frequency];
        if (frequencyValue !== undefined) {
          return Math.ceil(estimatedTimePerUnit * frequencyValue);
        }
      }
    }
    return 0;
  },
  [Constants.TaskCalculationMethod.TimeXPricePerHour]: (
    serviceDTO: DTOs.ServiceDTO,
    taskDTO: DTOs.TaskDTO
  ) => {
    if (
      resolveTaskDisplayedAllocationOfResponsibility(taskDTO) ===
      Constants.TaskAllocationResponsibility.Client
    ) {
      return 0;
    }
    const multiplicator = getFrequencyMultiplicator(taskDTO, serviceDTO);
    const estimatedTimePerUnit = getTaskEstimatedTimePerUnit(taskDTO);

    return estimatedTimePerUnit * multiplicator;
  },
  [Constants.TaskCalculationMethod.AmountXTime]: (
    serviceDTO: DTOs.ServiceDTO,
    taskDTO: DTOs.TaskDTO
  ) => {
    if (
      resolveTaskDisplayedAllocationOfResponsibility(taskDTO) ===
      Constants.TaskAllocationResponsibility.Client
    ) {
      return 0;
    }
    return 0;
  },
  [Constants.TaskCalculationMethod.FrequencyXAmountXTime]: (
    serviceDTO: DTOs.ServiceDTO,
    taskDTO: DTOs.TaskDTO
  ) => {
    if (
      resolveTaskDisplayedAllocationOfResponsibility(taskDTO) ===
      Constants.TaskAllocationResponsibility.Client
    ) {
      return 0;
    }
    const amountVariable = getTaskCalculationVariable(serviceDTO, taskDTO);
    const multiplicator = getFrequencyMultiplicator(taskDTO, serviceDTO);
    const estimatedTimePerUnit = getTaskEstimatedTimePerUnit(taskDTO);

    return Math.ceil(
      multiplicator * Number(amountVariable) * estimatedTimePerUnit
    );
  },
};

export function calculateEstimatedTimeInMinutes(
  serviceDTO: DTOs.ServiceDTO,
  taskDTO: DTOs.TaskDTO
): number {
  const taskCostProcessor =
    calculationMethodProcessorsMap[taskDTO.data.calculationMethod];
  if (!!taskCostProcessor) {
    return taskCostProcessor(serviceDTO, taskDTO);
  }

  return 0;
}

export function handleAnnualPayrollRoutinesState(
  nextServices: DTOs.ServiceDTO[]
) {
  const annualPayrollRoutines = nextServices.find(
    (service) =>
      service.data.code === Constants.ServiceCode.AnnualPayrollRoutines
  );
  if (annualPayrollRoutines) {
    annualPayrollRoutines.state.isSelected = nextServices.some(
      (eachService) =>
        eachService.data.code ===
          Constants.ServiceCode.PayrollAndExpenseAndTravelInvoiceManagement &&
        eachService.state.isSelected
    );
  }
}

export function handleYearlyServiceState(nextServices: DTOs.ServiceDTO[]) {
  const yearlyService = nextServices.find(
    (service) =>
      service.data.code ===
      Constants.ServiceCode.YearlyInternalDocumentationAndFormalities
  );
  if (yearlyService) {
    yearlyService.state.isSelected = nextServices.some(
      (eachService) =>
        eachService.state.isSelected &&
        eachService.state.isVisibleInServicesPage
    );
  }
}

export function handleInterimListedCompaniesState(
  nextServices: DTOs.ServiceDTO[]
) {
  const interimListedCompanies = nextServices.find(
    (service) =>
      service.data.code === Constants.ServiceCode.InterimListedCompanies
  );

  const isMainServiceSelected = nextServices.some(
    (eachService) =>
      eachService.data.code === Constants.ServiceCode.PeriodReporting &&
      eachService.state.isSelected
  );
  if (interimListedCompanies) {
    interimListedCompanies.state.canBeSelectedInServicePage =
      isMainServiceSelected;
    if (!isMainServiceSelected) {
      interimListedCompanies.state.isSelected = isMainServiceSelected;
    }
  }
}

export function handleExtraServiceState(
  nextServices: DTOs.ServiceDTO[],
  isFinalorOutdatedVersion: boolean
) {
  const extraService = nextServices.find(
    (service) => service.data.code === Constants.ServiceCode.ExtraServices
  );
  if (extraService) {
    const q1101 = extraService.data.questions.find(
      (question) =>
        question.data.code === Constants.ExtraServicesQuestions.Q1101
    );
    const wasAnsweredByUser = q1101?.state.isFilled === true;

    const anyOtherServiceSelected = nextServices.some(
      (eachService) =>
        eachService.data.code !== Constants.ServiceCode.EngagementCounseling &&
        eachService.state.isSelected &&
        eachService.state.isVisibleInServicesPage
    );

    extraService.state.isSelected =
      wasAnsweredByUser ||
      (!isFinalorOutdatedVersion && anyOtherServiceSelected);
  }
}

export function handleEngagementCounselingState(
  nextServices: DTOs.ServiceDTO[]
) {
  const engagementCounselingService = nextServices.find(
    (service) =>
      service.data.code === Constants.ServiceCode.EngagementCounseling
  );

  const anyOtherServiceSelected = nextServices.some(
    (eachService) =>
      eachService.data.code !== Constants.ServiceCode.EngagementCounseling &&
      eachService.state.isSelected &&
      eachService.state.isVisibleInServicesPage
  );
  if (engagementCounselingService) {
    engagementCounselingService.state.canBeSelectedInServicePage =
      !anyOtherServiceSelected;

    if (anyOtherServiceSelected) {
      engagementCounselingService.state.isSelected = false;
    }

    nextServices.forEach((service) => {
      if (
        service.data.code !== Constants.ServiceCode.EngagementCounseling &&
        service.data.code !== Constants.ServiceCode.GeneralInformation
      ) {
        if (
          engagementCounselingService.state.canBeSelectedInServicePage &&
          engagementCounselingService.state.isSelected
        ) {
          service.state.isSelected = false;
          service.state.canBeSelectedInServicePage = false;
        } else {
          service.state.canBeSelectedInServicePage = true;
        }
      }
    });
    if (!engagementCounselingService.state.isSelected) {
      handleAnnualReportingListedCompaniesState(nextServices);
      handleInterimListedCompaniesState(nextServices);
    }
  }
}

export function handleAuditServices(nextServices: DTOs.ServiceDTO[]) {
  const params = new URLSearchParams(window.location.search);
  const businessUnit = params.get("unit") || Constants.BusinessUnit.Outsourcing;

  if (businessUnit === Constants.BusinessUnit.Audit) {
    const filteredServices = nextServices.filter(
      (service) =>
        service.data.code !== Constants.ServiceCode.GeneralInformation
    );
    const selectedService = filteredServices.filter(
      (service) => service.state.isSelected
    );
    filteredServices.forEach((service) => {
      if (selectedService.length > 0) {
        if (service.data.code !== selectedService[0].data.code) {
          service.state.canBeSelectedInServicePage = false;
        }
      } else {
        service.state.canBeSelectedInServicePage = true;
      }
    });
  }
}

export function handleTaxEngagementLetters(nextServices: DTOs.ServiceDTO[]) {
  const params = new URLSearchParams(window.location.search);
  const businessUnit = params.get("unit") || Constants.BusinessUnit.Outsourcing;

  if (businessUnit === Constants.BusinessUnit.Tax) {
    const selectedService = nextServices.find(
      (eachService) =>
        eachService.state.isSelected &&
        eachService.state.isVisibleInServicesPage
    );
    if (selectedService) {
      nextServices.forEach((eachService) => {
        if (eachService.data.code !== selectedService.data.code) {
          eachService.state.canBeSelectedInServicePage = false;
        }
      });
    } else {
      nextServices.forEach((eachService) => {
        eachService.state.canBeSelectedInServicePage = true;
      });
    }
  }
}

export function handleAnnualReportingListedCompaniesState(
  nextServices: DTOs.ServiceDTO[]
) {
  const annualReportingListedCompanies = nextServices.find(
    (service) =>
      service.data.code === Constants.ServiceCode.AnnualReportingListedCompanies
  );
  const isMainServiceSelected = nextServices.some(
    (eachService) =>
      eachService.data.code === Constants.ServiceCode.AnnualReporting &&
      eachService.state.isSelected
  );
  if (annualReportingListedCompanies) {
    annualReportingListedCompanies.state.canBeSelectedInServicePage =
      isMainServiceSelected;
    if (!isMainServiceSelected) {
      annualReportingListedCompanies.state.isSelected = isMainServiceSelected;
    }
  }
}

export function handleAnnualReportingState(nextServices: DTOs.ServiceDTO[]) {
  const annualReporting = nextServices.find(
    (service) => service.data.code === Constants.ServiceCode.AnnualReporting
  );
  const service4DTO = nextServices.find(
    (serviceDTO) =>
      serviceDTO.data.code ===
        Constants.ServiceCode.OtherAccountAndReconciliation &&
      serviceDTO.state.isSelected
  );
  if (annualReporting && service4DTO) {
    const taskDTOs = annualReporting.data.tasks.filter(
      (taskDTO) =>
        taskDTO.data.code === Constants.AnnualReportingTasks.T0612 ||
        taskDTO.data.code === Constants.AnnualReportingTasks.T0613
    );

    if (!!service4DTO) {
      const calculationQuestionOverride = findQuestionInService(
        service4DTO,
        Constants.OtherAccountAndReconciliationQuestions.Q0405
      );

      taskDTOs.forEach((taskDTO) => {
        taskDTO.data.calculationQuestionOverride = calculationQuestionOverride;
      });
    } else {
      taskDTOs.forEach((taskDTO) => {
        taskDTO.data.calculationQuestionOverride = null;
      });
    }
  }
}

export function setEngagementConfigurationOwner(
  intermediateState: CoreInterfaces.AppState,
  questionGTOwner: DTOs.QuestionDTO
): void {
  let userValue = questionGTOwner.data.userValue;
  if (
    questionGTOwner.data.code === Constants.GeneralInformationQuestion.QT104
  ) {
    userValue = (questionGTOwner.data.userValue as string[])[0];
  }
  const employee: CoreInterfaces.ContactPersonItem =
    intermediateState.remoteData.gtContactPersons.find(
      (eachContactPerson) => eachContactPerson.key === userValue
    );
  intermediateState.currentConfiguration.owner = employee;
}

export function setNotSignedEmployeesUIDs(
  intermediateState: CoreInterfaces.AppState,
  modifiedQuestion: DTOs.QuestionDTO
) {
  if (modifiedQuestion.state.isShown) {
    intermediateState.currentConfiguration.notSigningEmployeesUIDs =
      modifiedQuestion.data.userValue !==
        Constants.HelpfulConstants.PleaseSelect &&
      Array.isArray(modifiedQuestion.data.userValue) &&
      modifiedQuestion.data.userValue.length > 0
        ? (modifiedQuestion.data.userValue as string[]).map(String)
        : null;
  }
}

export function isServiceTaskGroupSelected(
  questionDTO: DTOs.QuestionDTO,
  serviceTaskGroup: Constants.PartOfAnnualAccounts
) {
  return (
    questionDTO &&
    (questionDTO.data.userValue as string).includes(serviceTaskGroup)
  );
}

export function arrayMove(
  inputArray: Array<any>,
  oldIndex: number,
  newIndex: number
) {
  const arrCopy = [...inputArray];
  if (newIndex >= arrCopy.length) {
    let k = newIndex - arrCopy.length + 1;
    while (k--) {
      arrCopy.push(undefined);
    }
  }
  arrCopy.splice(newIndex, 0, arrCopy.splice(oldIndex, 1)[0]);

  return arrCopy;
}

export function onTaskOrderChange(
  serviceDTO: DTOs.ServiceDTO,
  params: GridRowOrderChangeParams,
  visibleRows: Array<
    | CoreInterfaces.CalculationServiceTableRow
    | CoreInterfaces.EngagementServiceTableRow
  >,
  dispatchFn: Function
) {
  const previousTargetTaskCode =
    params.targetIndex < params.oldIndex
      ? visibleRows[params.targetIndex - 1]?.taskDTO.data.code
      : visibleRows[params.targetIndex]?.taskDTO.data.code;
  dispatchFn({
    type: Constants.AppStateActions.TaskOrderChange,
    payload: {
      serviceDTO,
      taskDTO: params.row.taskDTO,
      previousTargetTaskCode,
    },
  });
}
